/**
 * BarcodeTabs Component - Tab navigation and view controls
 * Includes simulator tab for generating test barcodes
 */
import React from 'react';
import { Package, Box, Filter, LayoutGrid, LayoutList, Building2, QrCode, ScanSearch } from 'lucide-react';

type ViewMode = 'accordion' | 'cards' | 'list';
type ActiveTab = 'produtos' | 'estoque' | 'simulador' | 'validacao';

interface BarcodeTabsProps {
  activeTab: ActiveTab;
  setActiveTab: (tab: ActiveTab) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  activeFiltersCount: number;
}

export const BarcodeTabs: React.FC<BarcodeTabsProps> = ({
  activeTab,
  setActiveTab,
  viewMode,
  setViewMode,
  showFilters,
  setShowFilters,
  activeFiltersCount,
}) => {
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('products_barcode_view_mode', mode);
  };

  return (
    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1 inline-flex gap-1">
        <button
          onClick={() => setActiveTab('produtos')}
          className={`px-6 py-3 rounded-md font-semibold transition-all duration-200 flex items-center gap-2 ${
            activeTab === 'produtos'
              ? 'bg-amber-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Package className="w-5 h-5" />
          Tabela Fato (Produtos Cadastrados)
        </button>
        <button
          onClick={() => setActiveTab('estoque')}
          className={`px-6 py-3 rounded-md font-semibold transition-all duration-200 flex items-center gap-2 ${
            activeTab === 'estoque'
              ? 'bg-amber-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Box className="w-5 h-5" />
          Produtos em Estoque (GS1 Inteligente)
        </button>
        <button
          onClick={() => setActiveTab('simulador')}
          className={`px-6 py-3 rounded-md font-semibold transition-all duration-200 flex items-center gap-2 ${
            activeTab === 'simulador'
              ? 'bg-purple-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <QrCode className="w-5 h-5" />
          Simulador de Códigos
        </button>
        <button
          onClick={() => setActiveTab('validacao')}
          className={`px-6 py-3 rounded-md font-semibold transition-all duration-200 flex items-center gap-2 ${
            activeTab === 'validacao'
              ? 'bg-teal-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <ScanSearch className="w-5 h-5" />
          Validacao GS1
        </button>
      </div>

      {/* Controls: Filters + View Mode (hidden on simulator tab) */}
      {activeTab !== 'simulador' && activeTab !== 'validacao' && (
      <div className="flex gap-2">
        {/* Filter button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            showFilters || activeFiltersCount > 0
              ? 'bg-amber-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          <Filter className="w-4 h-4" />
          Filtros
          {activeFiltersCount > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
              {activeFiltersCount}
            </span>
          )}
        </button>

        {/* View mode toggle */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1 inline-flex gap-1">
          <button
            onClick={() => handleViewModeChange('accordion')}
            className={`px-3 py-2 rounded-md transition-all duration-200 flex items-center gap-2 ${
              viewMode === 'accordion'
                ? 'bg-amber-600 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            title="Agrupado por Cliente (Acordeão)"
          >
            <Building2 className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">Por Cliente</span>
          </button>
          <button
            onClick={() => handleViewModeChange('cards')}
            className={`px-3 py-2 rounded-md transition-all duration-200 flex items-center gap-2 ${
              viewMode === 'cards'
                ? 'bg-amber-600 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            title="Visualização em Cards com Códigos de Barras"
          >
            <LayoutGrid className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">Cards</span>
          </button>
          <button
            onClick={() => handleViewModeChange('list')}
            className={`px-3 py-2 rounded-md transition-all duration-200 flex items-center gap-2 ${
              viewMode === 'list'
                ? 'bg-amber-600 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            title="Visualização em Lista sem Códigos de Barras"
          >
            <LayoutList className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">Lista</span>
          </button>
        </div>
      </div>
      )}
    </div>
  );
};
